// ./src/components/features/VehicleTasks/VehicleTaskFilters/VehicleTaskFilters.tsx
//
// Vehicle task-specific filters wrapper around ExpenseFiltersToolbar.
// Adds task status filter as extraFilters (in drawer) and extraChips
// (in the active filters row).
//
// Draft state pattern:
// - Maintains draft value for status internally.
// - Exposes an ExtraFiltersHandle via ref so ExpenseFiltersDrawer can
//   call onOpen (sync applied → draft), onApply (commit draft → applied),
//   onClear (reset draft), and hasDraftValues (for Clear button visibility).
// - The extraFilters rendered in the drawer bind to draft value, so
//   selecting a status does NOT trigger a data reload until the user
//   clicks Apply.
// - extraChips bind to the APPLIED value (from props), so chips only
//   appear after Apply is clicked.

import React, { useCallback, useMemo, useRef, useState } from "react";
import { Badge, CloseButton } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { VEHICLE_TASK_STATUS_OPTIONS } from "@/config";
import type { VehicleTaskStatusValue } from "@/utils/api/VehicleTaskService";

import { ExpenseFiltersToolbar } from "@/components/features/ExpenseFiltersToolbar";
import { ExtraFiltersHandle } from "@/components/features/ExpenseFiltersToolbar/ExpenseFiltersDrawer";

import {
  VehicleTaskStatusSelector,
} from "../VehicleTaskStatusSelector";


// ============================================
// Types
// ============================================

export interface VehicleTaskFiltersProps {
  /** Handler for add button click */
  onAdd?: () => void;

  /** Handler for refresh button click */
  onRefresh?: () => void;

  /** Whether add button is disabled */
  addDisabled?: boolean;

  /** Whether refresh button is disabled */
  refreshDisabled?: boolean;

  /** Whether data is loading */
  loading?: boolean;

  /** Custom label for add button */
  addLabel?: string;

  /** Current applied status filter value */
  status?: VehicleTaskStatusValue | null;

  /** Callback when status is applied */
  onStatusChange?: (value: VehicleTaskStatusValue | null) => void;

  /** Enable keyboard shortcuts (default: true) */
  enableShortcuts?: boolean;

  /**
   * Callback emitting a combined filter object built from all enabled sources.
   * Passed through to ExpenseFiltersToolbar.
   */
  onFiltersChange?: (filters: Record<string, unknown> | null) => void;
}

// ============================================
// Component
// ============================================

const VehicleTaskFiltersComponent: React.FC<VehicleTaskFiltersProps> = ({
  onAdd,
  onRefresh,
  addDisabled = false,
  refreshDisabled = false,
  loading = false,
  addLabel,
  status = null,
  onStatusChange,
  enableShortcuts = true,
  onFiltersChange,
}) => {
  const { t } = useTranslation(["vehicleTasks", "common"]);

  // ============================================
  // Draft State (used inside the drawer only)
  // ============================================

  const [draftStatus, setDraftStatus] = useState<VehicleTaskStatusValue | null>(null);

  // Ref to track latest applied value for getValues
  const statusRef = useRef(status);
  statusRef.current = status;

  // ============================================
  // ExtraFiltersHandle — lifecycle contract with ExpenseFiltersDrawer
  // ============================================

  const extraFiltersRef = useRef<ExtraFiltersHandle>({
    onOpen: () => {
      setDraftStatus(status);
    },
    onApply: () => {
      onStatusChange?.(draftStatus);
      const values: Record<string, unknown> = {};
      if (draftStatus !== null) {
        values.taskStatus = draftStatus;
      }
      return values;
    },
    onClear: () => {
      setDraftStatus(null);
    },
    hasDraftValues: () => {
      return draftStatus !== null;
    },
    getValues: () => {
      const values: Record<string, unknown> = {};
      if (statusRef.current !== null) {
        values.taskStatus = statusRef.current;
      }
      return values;
    },
  });

  // Keep the ref callbacks up to date with latest state/props
  extraFiltersRef.current.onOpen = () => {
    setDraftStatus(status);
  };
  extraFiltersRef.current.onApply = () => {
    onStatusChange?.(draftStatus);
    const values: Record<string, unknown> = {};
    if (draftStatus !== null) {
      values.taskStatus = draftStatus;
    }
    return values;
  };
  extraFiltersRef.current.onClear = () => {
    setDraftStatus(null);
  };
  extraFiltersRef.current.hasDraftValues = () => {
    return draftStatus !== null;
  };
  extraFiltersRef.current.getValues = () => {
    const values: Record<string, unknown> = {};
    if (statusRef.current !== null) {
      values.taskStatus = statusRef.current;
    }
    return values;
  };

  // ============================================
  // Handlers — Chip removal (removes applied value directly)
  // ============================================

  const handleRemoveStatus = useCallback(() => {
    onStatusChange?.(null);
  }, [onStatusChange]);

  // ============================================
  // Build Extra Chips (based on APPLIED value, not draft)
  // ============================================

  const extraChips = useMemo(() => {
    if (status === null) return undefined;

    const config = VEHICLE_TASK_STATUS_OPTIONS.find((opt) => opt.value === status);
    if (!config) return undefined;

    return (
      <Badge
        key="task-status"
        variant="light"
        color={config.color}
        size="lg"
        radius="sm"
        pr={3}
        style={{ flexShrink: 0 }}
        rightSection={
          <CloseButton
            size="xs"
            variant="transparent"
            onClick={handleRemoveStatus}
            aria-label={t("common:buttons.clear")}
          />
        }
      >
        {t(`vehicleTasks:${config.labelKey}`)}
      </Badge>
    );
  }, [status, handleRemoveStatus, t]);

  // ============================================
  // Build Extra Filters Content (for drawer — uses DRAFT value)
  // ============================================

  const extraFilters = useMemo(() => {
    return (
      <VehicleTaskStatusSelector
        value={draftStatus}
        onChange={setDraftStatus}
        label={t("vehicleTasks:filters.status", { defaultValue: "Status" })}
      />
    );
  }, [draftStatus, t]);

  // ============================================
  // Render
  // ============================================

  return (
    <ExpenseFiltersToolbar
      entityType="vehicle-tasks"
      onAdd={onAdd}
      onRefresh={onRefresh}
      addDisabled={addDisabled}
      refreshDisabled={refreshDisabled}
      loading={loading}
      addLabel={addLabel}
      carFilterMode="single"
      showDateFilter={false}
      showTagFilter={false}
      enableShortcuts={enableShortcuts}
      extraFilters={extraFilters}
      extraChips={extraChips}
      extraFiltersRef={extraFiltersRef}
      onFiltersChange={onFiltersChange}
    />
  );
};

export const VehicleTaskFilters = React.memo(VehicleTaskFiltersComponent);